import { useAuth, useOrganizationList } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { useSingleSessionEnforcement } from '~/hooks/useSingleSessionEnforcement';
import { invalidateGraphQLToken } from '~/lib/graphql/graphQLClient';

interface OrganizationInitializerProps {
  children: React.ReactNode;
}

export const OrganizationInitializer = ({
  children,
}: OrganizationInitializerProps) => {
  const auth = useAuth();
  const [isReady, setIsReady] = useState(false);

  // Enforce single session policy - sign out if another session becomes active
  useSingleSessionEnforcement();

  const {
    userMemberships,
    setActive,
    isLoaded: orgListLoaded,
  } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });

  useEffect(() => {
    const initializeOrganization = async () => {
      if (!auth.isLoaded) {
        return;
      }

      // If user is not signed in, skip org initialization
      if (!auth.isSignedIn) {
        setIsReady(true);
        return;
      }

      // If user already has an active org, we're done
      if (auth.orgId) {
        setIsReady(true);
        return;
      }

      if (!orgListLoaded) {
        return;
      }

      // Wait for memberships to finish loading
      if (userMemberships?.isLoading) {
        return;
      }

      const memberships = userMemberships?.data ?? [];
      const hasMemberships = memberships.length > 0;

      // If user has memberships, set the first one as active
      if (hasMemberships) {
        const firstOrg = memberships[0].organization;
        try {
          await setActive({ organization: firstOrg.id });
          // Invalidate cached GraphQL token so next request gets fresh token with org context
          invalidateGraphQLToken();
          // setActive will trigger a re-render with the new orgId
          // The next effect run will see auth.orgId and set isReady
          return;
        } catch {
          // On error, proceed anyway
          setIsReady(true);
        }
      } else {
        // User truly has no memberships
        setIsReady(true);
      }
    };

    initializeOrganization();
  }, [
    auth.isLoaded,
    auth.isSignedIn,
    auth.orgId,
    auth.userId,
    orgListLoaded,
    userMemberships,
    setActive,
  ]);

  // Show loading while:
  // - Auth is loading
  // - User is signed in but we haven't determined org status yet
  const shouldShowLoading =
    !auth.isLoaded || (auth.isSignedIn && !isReady && !auth.orgId);

  if (shouldShowLoading) {
    return (
      <div
        className="flex align-items-center justify-content-center"
        style={{
          height: '100vh',
          backgroundColor: 'var(--bq-surface-body)',
        }}
      >
        <div className="text-center">
          <h2 style={{ color: 'var(--bq-text-primary)' }}>Loading...</h2>
          <p style={{ color: 'var(--bq-text-secondary)' }}>
            Setting up your workspace...
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
